import { useState, useRef, useEffect, useMemo } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { faCloudUpload, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import { useLocation, useNavigate } from "react-router-dom";
import infoImg from "../../Utils/assets/img/icons/info_icon.png";
import {
  GetRequest,
  PostRequest,
} from "../../Utils/hooks/ApiHandler";
import useLocalStorageState from "use-local-storage-state";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import {
  getModuleSeries,
} from "../../Utils/helpers/commonhelper";
import { AuthorizationCard } from "../../CommonComponents/AuthorizationCard"
import { UpgradePlan } from "../../CommonComponents/UpgradePlan"
import Spinner from "../../Utils/helpers/SpinLoader";

// Helpers to normalize incoming values (object | string | number) to string IDs
const idString = (msg) =>
  z.preprocess((val) => {
    if (val == null) return "";
    if (typeof val === "object") {
      if ("value" in val) return String(val.value ?? "");
      return "";
    }
    return String(val);
  }, z.string().min(1, msg));

//optional field
const OptionalIdString = (msg) =>
  z.preprocess((val) => {
    if (val == null) return "";
    if (typeof val === "object") {
      if ("value" in val) return String(val.value ?? "");
      return "";
    }
    return String(val);
  }, z.string());

const asDateString = (msg) =>
  z.preprocess((val) => {
    if (val instanceof Date) return val.toISOString().split("T")[0]; // YYYY-MM-DD format
    return val;
  }, z.string().min(1, msg));

// const CreateSalesOrderSchema = z.object({
//   contact: idString("Contact name is required"),
//   amountReceived: z
//     .string()
//     .min(1, "Amount Received is required")
//     .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
//       message: "Must be a positive number",
//     }),
//   bankCharges: z
//     .string()
//     .optional()
//     .refine((val) => val === "" || !isNaN(Number(val)), {
//       message: "Must be a number",
//     }),
//   taxDeducted: z.enum(["no", "yes"], {
//     required_error: "Select tax option",
//   }),
// notificationEmail: z
//   .array(z.enum(["email", "whatsapp", "sms"]))
//   .nonempty("Select at least one option"),
//   amountWithheld: z
//     .string()
//     .min(1, "Amount withheld is required")
//     .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
//       message: "Must be a positive number",
//     }),
//   tdsAccount: z.string().optional(),
//   salesOrderDate: asDateString("Payment date is required"),
//   shipmentDate: asDateString("Payment received date is required"),
//   paymentTerm: idString("Payment term is required"),
//   reference: z.string().min(1, "Reference is required"),
//   termsConditions: z.string().optional(),
//   attachment: z.any().optional(),
//   customerNote: z.string().optional(),
//   accountsReceivables: OptionalIdString(),
// });

const CreateSalesOrderSchema = z
  .object({
    contact: idString("Contact name is required"),
    amountReceived: z
      .string()
      .min(1, "Amount Received is required")
      .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
        message: "Amount should be greater than 0",
      }),
    bankCharges: z
      .string()
      .optional()
    // .refine((val) => val === "" || !isNaN(Number(val)), {
    //   message: "Must be a number",
    // })
    ,
    taxDeducted: z.enum(["no", "yes"], {
      required_error: "Select tax option",
    }),
    notificationEmail: z
      .array(z.enum(["email", "whatsapp", "sms"]))
      .nonempty("Select at least one option"),

    // by default optional, lekin validation me condition lagayenge
    amountWithheld: z.string().optional(),
    tdsAccount: z.string().optional(),

    salesOrderDate: asDateString("Payment date is required"),
    shipmentDate: asDateString("Payment received date is required"),
    paymentTerm: idString("Payment term is required"),
    reference: z.string().optional(),
    termsConditions: z.string().optional(),
    attachment: z.any().optional(),
    customerNote: z.string().optional(),
    accountsReceivables: OptionalIdString(),
  })
  .superRefine((data, ctx) => {
    if (data.taxDeducted === "yes") {
      if (
        !data.amountWithheld ||
        isNaN(Number(data.amountWithheld)) ||
        Number(data.amountWithheld) <= 0
      ) {
        ctx.addIssue({
          path: ["amountWithheld"],
          code: z.ZodIssueCode.custom,
          message: "Amount withheld is required and must be a positive number",
        });
      }
      if (!data.tdsAccount) {
        ctx.addIssue({
          path: ["tdsAccount"],
          code: z.ZodIssueCode.custom,
          message: "TDS Account is required when tax is deducted",
        });
      }
    }
  });

export const CreatePayment = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  });
  // rbac permission object
  const [PERMISSION, _] = useLocalStorageState("permission")
  const customer_id = customer?.customer_details?.customer_id;
  const token = customer?.token?.authorization || "";
  const rf = customer?.token?.authentication || "";
  const extras = { customer, setCustomer, rf, navigate };
  const headers = {
    Authorization: token,
    Authentication: rf,
  };
  const [draftClick, setDraftClicked] = useState(false);
  const [leadOwner, setLeadOwner] = useState([]);
  const [contactOption, setContactOption] = useState([]);
  const [contactId, setContactId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quotation, setQuotation] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [show, setShow] = useState(false);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [showTextareaIndex, setShowTextareaIndex] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [quotationModule, setQuotationModule] = useState([]);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrow = tomorrowDate.toISOString().split("T")[0];

  // permissions to handle
  const paymentGrant = PERMISSION.payment
  const isPayment = paymentGrant.isAuthorized;

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
    watch,
    trigger,
    reset,
    register,
  } = useForm({
    resolver: zodResolver(CreateSalesOrderSchema),
    mode: "onChange",
    defaultValues: {
      salesOrderDate: today,
      shipmentDate: tomorrow,
      taxDeducted: "no",
      notificationEmail: [],
    },
  });

  const itemAmountWithHeld = [
    { value: "upi", label: "UPI" },
    { value: "credit_card", label: "Credit Card" },
    { value: "cheque", label: "Cheque" },
    { value: "cash", label: "Cash" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "bank_remittance", label: "Bank Remittance" },
  ];

  const itemTaxType = [
    { value: "tds_receivable", label: " TDS Receivable" },
    { value: "prepaid_expenses", label: "Prepaid Expenses" },
    { value: "employee_advance", label: "Employee Advance" },
    { value: "advance_tax", label: "Advance Tax" },
  ];

  // quotations options
  const quotationOptions = useMemo(
    () =>
      quotation.map((q) => ({
        value: q.quotationId,
        label: q.quotationName,
        account_code: q.quotationCode,
      })),
    [quotation]
  );

  const emailOption = watch("notificationEmail");

  // Fetch All lead owner
  const fetchAllLeads = async () => {
    try {
      const res = await GetRequest(
        `users/users/fetchUserAsOwner/${customer_id}`,
        { headers },
        extras
      );
      if (res.status === "success") {
        const data = res.data.map((leads) => ({
          leadOwnerName: leads.full_name,
          leadOwnerId: leads.id,
        }));
        setLeadOwner(data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchAllLeads();
  }, []);

  const contactOptions = useMemo(
    () =>
      contactOption.map((conta) => ({
        value: conta.contactId,
        label: conta.contactName,
        company_name: conta.company_name,
        company_id: conta.company_id,
        billingAddresses: conta.billingAddresses || [],
        shippingAddresses: conta.shippingAddresses || [],
      })),
    [contactOption]
  );

  // Custom option component for better formatting
  const CustomOption1 = ({
    innerRef,
    innerProps,
    data,
    isSelected,
    isFocused,
  }) => (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        padding: "8px 12px",
        backgroundColor: isSelected
          ? "#007bff"
          : isFocused
            ? "#f8f9fa"
            : "white",
        color: isSelected ? "white" : "#333",
        cursor: "pointer",
      }}
    >
      <div style={{ fontWeight: "500" }}>{data.label}</div>
      <div
        style={{ fontSize: "12px", color: isSelected ? "#e9ecef" : "#6c757d" }}
      >
        {data.company_name || "No Company"}
      </div>
    </div>
  );

  useEffect(() => {
    async function fetchQuotation() {
      const data = await getModuleSeries(customer_id, "payment");
      setQuotationModule(`${data.prefix}` + "-" + `${data.incremental_number}`);
      // setValue(
      //   "salesOrderNumber",
      //   `${data.prefix}` + "-" + `${data.incremental_number}`
      // );
    }
    fetchQuotation();
  }, []);

  const handleChange = (e) => {
    setFile(e.target.files[0]);
    setValue("attachment", e.target.files[0]);
  };

  const handleClickButton = () => {
    fileInputRef.current.click();
  };

  // draft option
  const handleDraftClick = () => {
    setDraftClicked(true);
  };

  const handleCancel = () => {
    setDraftClicked(false);
    setTimeout(() => {
      if (onClose) onClose();
      navigate("/salesorder?type=invoice");
    }, 100);
  };

  // Fetch Owners
  const fetchOwners = async () => { };

  useEffect(() => {
    fetchOwners();
  }, []);

  const fetchAllContacts = async () => {
    try {
      const res = await GetRequest(
        `quotation/fetchContacts/${customer?.customer_details?.id}/${customer.customer_details.customer_id}/${customer?.customer_details?.user_type}`,
        { headers },
        extras
      );

      if (res.status !== "success") {
        return toast.error(res.message || "Failed to find contacts");
      }

      if (Array.isArray(res.data)) {
        const data = res.data.map((contact) => {
          let billingAddresses = [];

          if (contact.billing_address) {
            try {
              billingAddresses = JSON.parse(contact.billing_address);
            } catch (e) {
              console.error("Billing address parse error:", e);
            }
          }

          if (billingAddresses.length === 0) {
            billingAddresses.push({
              address: contact.address,
              city: contact.city_name,
              state: contact.state_name,
              country: contact.country_name,
              zipcode: contact.zipcode,
              selected: "selected",
              for: "billing",
            });
          }

          const shippingAddresses = [...billingAddresses];

          return {
            contactName: contact.contact_full_name,
            contactId: contact.id,
            company_id: contact.company_id,
            company_name: contact.company_name,
            billingAddresses,
            shippingAddresses,
          };
        });

        setContactOption(data);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      toast.error("Something went wrong while fetching contacts");
    }
  };

  useEffect(() => {
    fetchAllContacts();
  }, [selectedContact]);

  const customStyles = {
    control: (provided) => ({
      ...provided,
      padding: "0.1rem 0.75rem",
      minHeight: "50px",
      backgroundPosition: "right 0.4rem center",
      border: "1px solid #ccc",
    }),
    placeholder: (provided) => ({
      ...provided,
      margin: "1.2rem 0px 0rem",
      fontSize: "0.9rem",
    }),
    singleValue: (provided) => ({
      ...provided,
      margin: "1.2rem 0px 0rem",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      paddingTop: "0.1rem",
      paddingBottom: "0.1rem",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0.25rem",
    }),
  };

  const customStyle = {
    control: (provided) => ({
      ...provided,
      padding: "0rem 0.5rem 0rem",
      minHeight: "35px",
      maxHeight: 38,
      backgroundPosition: "right 0.4rem center",
      border: "1px solid #ccc",
      boxShadow: "none",
    }),
    placeholder: (provided) => ({
      ...provided,
      margin: "0rem 0px 0.2rem",
      fontSize: "0.9rem",
    }),
  };

  const salesOrderDate = watch("salesOrderDate");
  const shipmentDate = watch("shipmentDate");

  const qDate = new Date(salesOrderDate);
  const eDate = new Date(shipmentDate);

  // Fetch All quotations
  const fetchChartOfAccount = async () => {
    try {
      const res = await GetRequest(
        `masters/fetchChartOfAccount/${customer_id}`,
        { headers },
        extras
      );
      if (res.status === "success") {
        const data = res.data.map((quotation) => ({
          quotationName: quotation.account_name,
          quotationCode: quotation.account_code,
          quotationId: quotation.id,
        }));
        setQuotation(data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchChartOfAccount();
  }, [selectedContact]);

  useEffect(() => {
    if (salesOrderDate) {
      trigger("shipmentDate");
    }
  }, [salesOrderDate, trigger, shipmentDate]);

  useEffect(() => {
    const contactId = location?.state?.key2;
    const contactName = location?.state?.key3;

    if (contactId && contactName) {
      reset({
        contact: {
          value: contactId,
          label: contactName,
        },
        salesOrderDate: today,
        shipmentDate: tomorrow,
        taxDeducted: "no",
        notificationEmail: [],
      });
    } else {
      reset({
        salesOrderDate: today,
        shipmentDate: tomorrow,
        taxDeducted: "no",
        notificationEmail: [],
      });
    }
  }, [location, reset, today, tomorrow]);

  const invoiceId = location?.state?.key1;

  const onSubmit = async (data) => {
    if (!invoiceId) {
      toast.error(
        "Invoice is required to record a payment. Please open this page from an invoice.",
      );
      return;
    }
    setLoading(true);
    try {
      let status;
      if (draftClick) {
        status = "draft";
      } else {
        status = "pending";
      }

      const dataToSend = {
        customer_id: customer_id,
        contact_id: selectedContact?.contactId || data.contact,
        payment_date: data.salesOrderDate,
        payment_recieved_on: data.shipmentDate,
        payment_mode: data.paymentTerm,
        amount_recevied: data.amountReceived,
        bank_charges: data.bankCharges || 0,
        tax_deducted: data.taxDeducted,
        amount_deducted: data.amountWithheld || 0,
        tds_tax_account: data.tdsAccount || "",
        notification_method: data.notificationEmail,
        reference_number: data.reference,
        notes: data.customerNote || "",
        terms_condition: data.termsConditions || "",
        attachment: data.attachment,
        order_status: status,
        user_login_id: customer?.customer_details?.id,
        coa_id: data.accountsReceivables,
        invoice_id: invoiceId,
      };


      const res = await PostRequest(
        `/invoice/createPayment`,
        dataToSend,
        true,
        { headers },
        extras
      );

      if (res.status !== "success") {
        toast.error(res.message || "Failed to create payment");
        
      } else {
        toast.success(res.message || "Payment created successfully!");
        reset();
        setDraftClicked(false);
        setTimeout(() => {
          navigate("/salesorder?type=invoice");
        }, 300);
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong!");
    }finally{
      setLoading(false);
    }
  };

  const handleSelectChange = (name, selectedOption, fieldOnChange) => {
    fieldOnChange(selectedOption?.value || "");

    // Update local state for selected values
    if (name === "contact") {
      setSelectedContact(selectedOption);
      setCompanyId(selectedOption.company_id);
      setContactId(selectedOption.value);
    }
  };

  const taxDeducted = watch("taxDeducted");

  // Custom option component for better formatting (optional)
  const CustomOption2 = ({
    innerRef,
    innerProps,
    data,
    isSelected,
    isFocused,
  }) => (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        padding: "8px 12px",
        backgroundColor: isSelected
          ? "#007bff"
          : isFocused
            ? "#f8f9fa"
            : "white",
        color: isSelected ? "white" : "#333",
        cursor: "pointer",
      }}
    >
      <div style={{ fontWeight: "500" }}>{data.label}</div>
      <div
        style={{ fontSize: "12px", color: isSelected ? "#e9ecef" : "#6c757d" }}
      >
        {data.account_code || "no code"}
      </div>
    </div>
  );

  return (
    <>
      {loading && <Spinner text="Processing..."/>}
      {!isPayment && <UpgradePlan moduleName="Payment" />}
      {!paymentGrant?.isCreate ? <AuthorizationCard /> :
        <div
          style={{
            borderRadius: 7,
            background: "#fff",
            height: "calc(100vh - 12vh)",
            border: "1px solid lightblue",
            overflow: "clip",
          }}
        >
          <div
            className="header mb-0 position-sticky top-0 p-3 "
            style={{
              borderBottom: "1px solid lightblue",
              zIndex: 11,
              background: "#fff",
            }}
          >
            <div className="d-flex align-center justify-content-between">
              <div className="d-flex">
                <h3
                  className=" mb-0"
                  style={{
                    whiteSpace: "nowrap",
                    fontWeight: 300,
                    fontSize: "22px",
                  }}
                >
                  {" "}
                  Payment
                </h3>
                <p
                  className="mt-1 mx-3 text-success fw-semibold"
                  style={{ fontWeight: "14px" }}
                >
                  (#{quotationModule})
                </p>
              </div>
              <button
                className="btn btn-default-outline"
                aria-label="Go Back"
                type="button"
                onClick={handleCancel}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>
          <Form
            onSubmit={handleSubmit(onSubmit)}
            className="form-container"
            encType="multipart/form-data"
            style={{
              margin: 0,
              overflowY: "auto",
              height: "100%",
              paddingLeft: "0px",
              paddingTop: "12px",
              position: "relative",
            }}
          >
            <Row className="ms-0 w-100 mb-0">
              <Col md={2} style={{ borderRight: "1px solid #d4e7ed" }}>
                <div
                  style={{ position: "sticky", top: "1rem" }}
                  className=" mt-3"
                >
                  <ul className=" ps-3" style={{ listStyle: "none" }}>
                    <li className="position-relative border-start ps-3 py-2 pt-0 ps-4">
                      <span
                        className="position-absolute  start-0 translate-middle bg-primary rounded-circle"
                        style={{
                          width: 10,
                          height: 10,
                          top: 11,
                          border: "1px solid #0099ff",
                        }}
                      ></span>
                      <a
                        href="#companyDetailsSection"
                        style={{ color: "#676262" }}
                      >
                        {" "}
                        Company Details{" "}
                      </a>
                    </li>

                    <li className="position-relative border-start ps-3 py-2 ps-4">
                      <span
                        className="position-absolute top-50 start-0 translate-middle bg-primary rounded-circle"
                        style={{ width: 10, height: 10 }}
                      ></span>
                      <a
                        href="#termsAndConditionsSec"
                        style={{ color: "#676262" }}
                      >
                        {" "}
                        Terms & Conditions{" "}
                      </a>
                    </li>
                  </ul>
                  <hr />
                </div>
              </Col>

              <Col md={10} className="ps-3">
                <Row className="ms-0 w-100" id="companyDetailsSection">
                  <h3
                    className=" mb-4 pt-3"
                    style={{
                      whiteSpace: "nowrap",
                      fontWeight: 300,
                      fontSize: "19px",
                    }}
                  >
                    {" "}
                    Company Details
                  </h3>

                  <Row className="ms-0 w-100">
                    <Col md={6} className="ps-0">
                      <Col md={12}>
                        <Form.Group
                          className="mb-3 focus-z-index position-relative"
                          controlId="formContact"
                        >
                          <Form.Label style={{ marginBottom: 1 }}>
                            Contact <span className="text-danger">*</span>
                          </Form.Label>


                          <Controller
                            name="contact"
                            control={control}
                            rules={{ required: "Contact is required" }}
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={contactOptions}
                                placeholder="Select Contact"
                                classNamePrefix="react-select"
                                styles={customStyles}
                                isDisabled
                                components={{ Option: CustomOption1 }}
                                value={
                                  contactOptions?.find(
                                    (opt) => opt.value === field.value?.value
                                  ) || field.value
                                }
                                onChange={(selected) => {
                                  field.onChange(selected);
                                  handleSelectChange(
                                    "contact",
                                    selected,
                                    field.onChange
                                  );
                                }}
                              />
                            )}
                          />

                          {errors.contact && (
                            <p
                              className="text-danger mt-1"
                              style={{ fontSize: "12px" }}
                            >
                              {errors.contact.message}
                            </p>
                          )}
                        </Form.Group>
                      </Col>

                      <Row>
                        <Col md={12}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative"
                            controlId="formReference"
                          >
                            <Form.Label style={{ marginBottom: 1 }}>
                              Reference 
                            </Form.Label>
                            <Controller
                              name="reference"
                              control={control}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  type="text"
                                  placeholder="Enter Reference"
                                />
                              )}
                            />
                            <img
                              src={infoImg || "/placeholder.svg"}
                              alt=""
                              style={{
                                width: 25,
                                height: 25,
                                position: "absolute",
                                right: 10,
                                top: 13,
                              }}
                            />
                            {errors.reference && (
                              <p
                                className="text-danger mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                {errors.reference.message}
                              </p>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>



                      <Row>
                        <Col md={6}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative"
                            controlId="formQuoteDate"
                          >
                            <Form.Label style={{ marginBottom: 1 }}>
                              Invoice Order Date{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>

                            <Controller
                              name="salesOrderDate"
                              control={control}
                              rules={{
                                required: "Ivoice date  is required",
                                validate: (value) => {
                                  const qDate = new Date(value);
                                  const tDate = new Date(today);

                                  if (qDate < tDate) {
                                    return "Payment date cannot be in the past";
                                  }
                                  return true;
                                },
                              }}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  type="date"
                                  placeholder="Select Payment Date"
                                  min={today}
                                  isInvalid={!!errors.salesOrderDate}
                                />
                              )}
                            />

                            {errors.salesOrderDate && (
                              <p
                                className="text-danger mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                {errors.salesOrderDate.message}
                              </p>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative"
                            controlId="formExpiryDate"
                          >
                            <Form.Label style={{ marginBottom: 1 }}>
                              Payment Received On{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>

                            <Controller
                              name="shipmentDate"
                              control={control}
                              rules={{
                                required: "Payment Received Date is required",
                                validate: (value) => {
                                  const qDate = new Date(salesOrderDate);
                                  const eDate = new Date(value);
                                  if (eDate <= qDate) {
                                    return "Payment received on must be after invoce order date";
                                  }
                                  return true;
                                },
                              }}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  type="date"
                                  placeholder="Select received on Date"
                                  min={salesOrderDate}
                                />
                              )}
                            />

                            {eDate < qDate && (
                              <p
                                className="text-danger mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                Payment received on date must be after payment date
                              </p>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative"
                            controlId="formPaymentTerm"
                          >
                            <Form.Label
                              className="w-auto"
                              style={{ marginBottom: 1 }}
                            >
                              Payment Mode <span className="text-danger">*</span>
                            </Form.Label>
                            <Controller
                              name="paymentTerm"
                              control={control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  options={itemAmountWithHeld}
                                  placeholder="Select Payment Term"
                                  classNamePrefix="react-select"
                                  styles={customStyles}
                                  value={itemAmountWithHeld.find(
                                    (opt) => opt.value === field.value
                                  )}
                                  onChange={(selected) =>
                                    handleSelectChange(
                                      "paymentTerm",
                                      selected,
                                      field.onChange
                                    )
                                  }
                                />
                              )}
                            />
                            {errors.paymentTerm && (
                              <p
                                className="text-danger mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                {errors.paymentTerm.message}
                              </p>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group
                            className="mb-3 focus-z-index position-relative"
                            controlId="formCompanyName"
                          >
                            <Form.Label
                              className="w-auto"
                              style={{ marginBottom: 1 }}
                            >
                              Accounts Receivables (COA)
                            </Form.Label>
                            <Controller
                              name="accountsReceivables"
                              control={control}
                              rules={{ required: "accountsReceivables required" }}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  options={quotationOptions}
                                  placeholder="Select COA"
                                  classNamePrefix="react-select"
                                  components={{
                                    Option: CustomOption2,
                                  }}
                                  value={
                                    quotationOptions?.find(
                                      (opt) => opt.value === field.value
                                    ) || ""
                                  }
                                  onChange={(selected) =>
                                    handleSelectChange(
                                      "quotation",
                                      selected,
                                      field.onChange
                                    )
                                  }
                                  styles={customStyles}
                                />
                              )}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3 focus-z-index position-relative">
                            <Form.Label>
                              Amount Received (INR){" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Controller
                              name="amountReceived"
                              control={control}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  type="number"
                                  placeholder="Enter amount"
                                />
                              )}
                            />
                            {errors.amountReceived && (
                              <Form.Text className="text-danger">
                                {errors.amountReceived.message}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group className="mb-3 focus-z-index position-relative">
                            <Form.Label>Bank Charges (if any)</Form.Label>
                            <Controller
                              name="bankCharges"
                              control={control}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  type="number"
                                  placeholder="Enter charges"
                                />
                              )}
                            />
                            {errors.bankCharges && (
                              <Form.Text className="text-danger">
                                {errors.bankCharges.message}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          {/* Tax Deducted */}
                          <Form.Group className="mb-3 ">
                            <div className="d-flex align-items-start gap-2">
                              <span className="mt-1">Tax deducted?</span>
                              <div>
                                <Controller
                                  name="taxDeducted"
                                  control={control}
                                  render={({ field }) => (
                                    <>
                                      <div className="d-flex gap-4">
                                        <div className="d-flex align-items-start">
                                          <Form.Check
                                            className="mb-3 focus-z-index custom-radio"
                                            inline
                                            type="radio"
                                            value="no"
                                            checked={field.value === "no"}
                                            onChange={(e) =>
                                              field.onChange(e.target.value)
                                            }
                                            label=""
                                          />
                                          <span className="mt-1">No Tax deducted</span>
                                        </div>
                                        <div className="d-flex align-items-start">
                                          <Form.Check
                                            className="mb-3 focus-z-index custom-radio "
                                            inline
                                            type="radio"
                                            label=""
                                            value="yes"
                                            checked={field.value === "yes"}
                                            onChange={(e) =>
                                              field.onChange(e.target.value)
                                            }
                                          />
                                          <span className="mt-1">Tax deducted</span>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                />
                              </div>
                            </div>
                            {errors.taxDeducted && (
                              <Form.Text className="text-danger">
                                {errors.taxDeducted.message}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
                      <style jsx>{`
        .custom-radio .form-check-input {
          width: 24px;
          height: 24px;
          background-color: var(--custom-white);
          border: 1px solid var(--input-border);
        }
        .custom-radio .form-check-input:checked {
          width: 24px;
          height: 24px;
          background-color: var(--primary-color);
          border-color: var(--primary-color);
        }
      `}</style>
                      {taxDeducted === "yes" && (
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3 focus-z-index position-relative">
                              <Form.Label>Amount Withheld <sup className='text-danger'>*</sup></Form.Label>
                              <Controller
                                name="amountWithheld"
                                control={control}
                                render={({ field }) => (
                                  <Form.Control
                                    {...field}
                                    type="number"
                                    placeholder="Enter amount"
                                  />
                                )}
                              />
                              {errors.amountWithheld && (
                                <Form.Text className="text-danger">
                                  {errors.amountWithheld.message}
                                </Form.Text>
                              )}
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group className="mb-3 focus-z-index position-relative">
                              <Form.Label>TDS Tax Account <sup className='text-danger'>*</sup></Form.Label>
                              <Controller
                                name="tdsAccount"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    options={itemTaxType}
                                    placeholder="Select Payment Term"
                                    classNamePrefix="react-select"
                                    styles={customStyles}
                                    value={itemTaxType.find(
                                      (opt) => opt.value === field.value
                                    )}
                                    onChange={(selected) =>
                                      handleSelectChange(
                                        "tdsAccount",
                                        selected,
                                        field.onChange
                                      )
                                    }
                                  />
                                )}
                              />
                              {errors.tdsAccount && (
                                <Form.Text className="text-danger">
                                  {errors.tdsAccount.message}
                                </Form.Text>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                      )}
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <div className="d-flex gap-2">
                              <span>Notification <sup className='text-danger'>*</sup></span>
                              <div>
                                {/* <Controller
            name="notificationEmail"
            control={control}
            render={({ field }) => {
              const { value = [], onChange } = field;

              const handleCheck = (option: string, checked: boolean) => {
                if (checked) {
                  onChange([...value, option]); // add
                } else {
                  onChange(value.filter((v) => v !== option)); // remove
                }
              };

              return (
                <div className="d-flex gap-3">
                  <div className="d-flex">
                    <Form.Check
                      inline
                      type="checkbox"
                      value="email"   // ✅ important
                      checked={value.includes("email")}
                      onChange={(e) =>
                        handleCheck("email", e.target.checked)
                      }
                      label=""
                    />
                    <span>Email</span>
                  </div>

                  <div className="d-flex">
                    <Form.Check
                      inline
                      type="checkbox"
                      value="whatsapp"   // ✅ important
                      checked={value.includes("whatsapp")}
                      onChange={(e) =>
                        handleCheck("whatsapp", e.target.checked)
                      }
                      label=""
                    />
                    <span>Whatsapp</span>
                  </div>

                  <div className="d-flex">
                    <Form.Check
                      inline
                      type="checkbox"
                      value="sms"   // ✅ important
                      checked={value.includes("sms")}
                      onChange={(e) =>
                        handleCheck("sms", e.target.checked)
                      }
                      label=""
                    />
                    <span>Sms</span>
                  </div>
                </div>
              );
            }}
          /> */}

                                <Controller
                                  name="notificationEmail"
                                  control={control}
                                  render={({ field }) => {
                                    const { value = [], onChange } = field;

                                    const handleCheck = (
                                      option,
                                      checked
                                    ) => {
                                      if (checked) {
                                        onChange([...value, option]); // add option
                                      } else {
                                        onChange(
                                          value.filter((v) => v !== option)
                                        ); // remove option
                                      }
                                    };

                                    return (
                                      <div className="d-flex gap-3">
                                        <div className="d-flex">
                                          <Form.Check
                                            inline
                                            type="checkbox"
                                            checked={value.includes("email")}
                                            // ✅ force "email", don’t use e.target.value
                                            onChange={(e) =>
                                              handleCheck(
                                                "email",
                                                e.target.checked
                                              )
                                            }
                                            label=""
                                          />
                                          <span>Email</span>
                                        </div>

                                        <div className="d-flex">
                                          <Form.Check
                                            inline
                                            type="checkbox"
                                            checked={value.includes("whatsapp")}
                                            onChange={(e) =>
                                              handleCheck(
                                                "whatsapp",
                                                e.target.checked
                                              )
                                            }
                                            label=""
                                          />
                                          <span>Whatsapp</span>
                                        </div>

                                        <div className="d-flex">
                                          <Form.Check
                                            inline
                                            type="checkbox"
                                            checked={value.includes("sms")}
                                            onChange={(e) =>
                                              handleCheck("sms", e.target.checked)
                                            }
                                            label=""
                                          />
                                          <span>Sms</span>
                                        </div>
                                      </div>
                                    );
                                  }}
                                />
                              </div>
                            </div>
                            {errors.notificationEmail && (
                              <Form.Text className="text-danger">
                                {errors.notificationEmail.message}
                              </Form.Text>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <hr />

                  <Row id="notes">
                    <Col md={8}>
                      <h3
                        className=" mb-3"
                        style={{
                          whiteSpace: "nowrap",
                          fontWeight: 300,
                          fontSize: "19px",
                        }}
                      >
                        {" "}
                        Customer Note{" "}
                      </h3>

                      <Form.Group className="mb-3" controlId="formCustomerNote">
                        <Controller
                          name="customerNote"
                          control={control}
                          render={({ field }) => (
                            <textarea
                              {...field}
                              className="normalTextarea"
                              placeholder="Enter a note for the customer"
                              style={{ minHeight: 81 }}
                            />
                          )}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row id="termsAndConditionsSec">
                    <Col md={8}>
                      <h3
                        className=" mb-3"
                        style={{
                          whiteSpace: "nowrap",
                          fontWeight: 300,
                          fontSize: "19px",
                        }}
                      >
                        {" "}
                        Terms & Conditions{" "}
                      </h3>

                      <Form.Group
                        className="mb-3"
                        controlId="formTermsConditions"
                      >
                        <Controller
                          name="termsConditions"
                          control={control}
                          render={({ field }) => (
                            <textarea
                              {...field}
                              className="normalTextarea"
                              placeholder="Enter the terms and conditions of your business to be displayed in your transaction"
                              style={{ minHeight: 81 }}
                            />
                          )}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                      <h3
                        className=" mb-3"
                        style={{
                          whiteSpace: "nowrap",
                          fontWeight: 300,
                          fontSize: "19px",
                        }}
                      >
                        {" "}
                        Add Attachment{" "}
                      </h3>
                      <div
                        style={{
                          border: "2px dashed #ccc",
                          borderRadius: "10px",
                          padding: "10px 20px",
                          textAlign: "center",
                          background: "#f9f9f9",
                          width: "100%",
                          maxWidth: "400px",
                          margin: "-1px auto",
                        }}
                      >
                        <p style={{ marginBottom: "5px", color: "#666" }}>
                          {file
                            ? `Selected: ${file.name}`
                            : "Drag & drop or click to upload a file"}
                        </p>

                        <button
                          type="button"
                          onClick={handleClickButton}
                          style={{
                            padding: "5px 20px",
                            backgroundColor: "#0099ff",
                            color: "#fff",
                            borderRadius: "5px",
                            cursor: "pointer",
                            border: "none",
                            fontSize: "16px",
                          }}
                        >
                          <FontAwesomeIcon icon={faCloudUpload} /> Browse File
                        </button>

                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleChange}
                          style={{ display: "none" }}
                        />
                      </div>
                      <div
                        style={{
                          // marginTop: "8px",
                          width: "100%",
                          maxWidth: "400px",
                          textAlign: "center", // Center text to match the box above
                        }}
                      >
                        <p
                          style={{
                            color: "#a5a5a5",
                            fontSize: "11px",
                            fontWeight: "400",
                            margin: 0,
                          }}
                        >
                          Accepted file types are .jpg, .png, .pdf, max file size
                          2MB
                        </p>
                      </div>
                    </Col>
                  </Row>
                </Row>

                <p style={{ height: "10rem" }}></p>

                <div
                  className="bg-white position-fixed "
                  style={{
                    paddingRight: "2rem",
                    height: 75,
                    bottom: "1rem",
                    right: "1.4rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "end",
                    width: "93.4%",
                    zIndex: 11,
                    borderTop: "1px solid lightblue",
                  }}
                >
                  <Button
                    className="btn btn-default-outline me-2"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>

                  <Button
                    className="btn btn-default "
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? "Loading..." : " Submit"}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      }
    </>
  );
};
